// Iter 82.9 Sprint 2 — Payroll Preview Dashboard panel.
// Operator-mandated approval-readiness payload + cohort metrics +
// variance + departmental rollup + component totals.
import { useCallback, useEffect, useState } from "react";
import {
    CheckCircle, XCircle, Clock, Warning, Users, UserPlus,
    UserMinus, Bank, CurrencyInr,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../../lib/api";
import { Badge } from "../../../components/ui/badge";
import { toast } from "sonner";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const READINESS_TONE = {
    READY:              { tone: "bg-emerald-50 text-emerald-800 border-emerald-200", Icon: CheckCircle },
    PENDING_CALCULATE:  { tone: "bg-slate-50 text-slate-700 border-slate-200", Icon: Clock },
    BLOCKED_SUBMIT:     { tone: "bg-rose-50 text-rose-800 border-rose-200", Icon: XCircle },
    BLOCKED_APPROVE:    { tone: "bg-amber-50 text-amber-800 border-amber-200", Icon: Warning },
};

function ReadinessCard({ readiness }) {
    const meta = READINESS_TONE[readiness?.status] || READINESS_TONE.PENDING_CALCULATE;
    const Icon = meta.Icon;
    return (
        <div className={`border rounded-lg p-4 flex items-start gap-3 ${meta.tone}`} data-testid="preview-readiness">
            <Icon size={28} weight="fill" className="flex-shrink-0" />
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <span className="text-xs uppercase tracking-wide">Approval Readiness</span>
                    <Badge variant="outline" data-testid="preview-readiness-status">{readiness?.status || "—"}</Badge>
                </div>
                <p className="text-sm mt-1" data-testid="preview-readiness-reason">{readiness?.reason}</p>
                {readiness?.blocks_submit_count > 0 && (
                    <p className="text-xs mt-1" data-testid="preview-blocks-submit">
                        {/* Iter 111.1 UAT · Obs 5 — Dynamic singular / plural
                            grammar. Exactly-one exception uses "blocks" +
                            singular noun; multiple exceptions use "block"
                            + plural noun. */}
                        🚫 <strong>{readiness.blocks_submit_count}</strong>{" "}
                        {readiness.blocks_submit_count === 1
                            ? "critical exception blocks Submit."
                            : "critical exceptions block Submit."}
                    </p>
                )}
                {readiness?.blocks_approve_count > 0 && readiness?.blocks_submit_count === 0 && (
                    <p className="text-xs mt-1" data-testid="preview-blocks-approve">
                        ⚠ <strong>{readiness.blocks_approve_count}</strong>{" "}
                        {readiness.blocks_approve_count === 1
                            ? "critical/high exception blocks Approve."
                            : "critical/high exceptions block Approve."}
                    </p>
                )}
            </div>
        </div>
    );
}

function MetricChip({ icon: Icon, label, value, tone, testId }) {
    return (
        <div className={`border rounded-md px-3 py-2 flex items-center gap-2 ${tone || "bg-white"}`} data-testid={testId}>
            <Icon size={18} className="text-slate-400" />
            <div className="flex flex-col">
                <span className="text-[10px] uppercase text-slate-500 tracking-wide">{label}</span>
                <span className="font-mono font-semibold text-sm">{value}</span>
            </div>
        </div>
    );
}

export default function PayrollPreviewPanel({ runId, run }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get(`/payroll/run/${runId}/preview-dashboard`);
            setData(r.data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load preview"));
        } finally {
            setLoading(false);
        }
    }, [runId]);

    useEffect(() => { reload(); }, [reload]);

    if (loading) return <div className="p-6 text-slate-500" data-testid="preview-loading">Loading preview…</div>;
    if (!data) return null;
    const cohort = data.cohort_metrics || {};
    const variance = data.variance_to_prior_month || {};
    const thresh = data.variance_thresholds_pct || {};
    const totals = data.totals || {};
    const varianceBreached = variance.pct !== null
        && variance.pct !== undefined
        && Math.abs(variance.pct) > (thresh.total_payroll_mom || 5);

    return (
        <div className="space-y-4 pt-3" data-testid="preview-panel">
            <ReadinessCard readiness={data.approval_readiness} />

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                <MetricChip
                    icon={Users}
                    label="Employee Count"
                    value={totals.headcount || 0}
                    testId="metric-headcount"
                />
                <MetricChip
                    icon={CurrencyInr}
                    label="Gross Payroll"
                    value={inr(totals.gross_earnings)}
                    testId="metric-gross"
                />
                <MetricChip
                    icon={CurrencyInr}
                    label="Total Deductions"
                    value={inr(totals.gross_deductions)}
                    testId="metric-deductions"
                />
                <MetricChip
                    icon={CurrencyInr}
                    label="Net Payable"
                    value={inr(totals.net_pay)}
                    testId="metric-net"
                />
                <MetricChip
                    icon={UserPlus}
                    label="Joiners in Period"
                    value={cohort.joiners_count || 0}
                    testId="metric-joiners"
                />
                <MetricChip
                    icon={UserMinus}
                    label="Leavers in Period"
                    value={cohort.leavers_count || 0}
                    testId="metric-leavers"
                />
                <MetricChip
                    icon={Bank}
                    label="Missing Bank Details"
                    value={cohort.missing_bank_count || 0}
                    testId="metric-missing-bank"
                    tone={cohort.missing_bank_count > 0 ? "bg-rose-50 border-rose-200" : ""}
                />
                <MetricChip
                    icon={Warning}
                    label="Negative Net Blockers"
                    value={cohort.negative_net_blockers_count || 0}
                    testId="metric-negative-net"
                    tone={cohort.negative_net_blockers_count > 0 ? "bg-rose-50 border-rose-200" : ""}
                />
            </div>

            <div className="border rounded-lg bg-white p-4" data-testid="preview-variance">
                <div className="text-xs uppercase tracking-wide text-slate-500">Variance vs Prior Locked Run (D10 — tunable per run)</div>
                {variance.pct === null || variance.pct === undefined ? (
                    <div className="text-sm text-slate-400 mt-1">No prior locked run to compare.</div>
                ) : (
                    <div className="flex items-baseline gap-3 mt-2 flex-wrap">
                        <span className={`text-2xl font-mono font-semibold ${varianceBreached ? "text-rose-700" : "text-emerald-700"}`}>
                            {variance.pct > 0 ? "+" : ""}{Number(variance.pct).toFixed(2)}%
                        </span>
                        <span className="text-sm text-slate-500">({variance.delta > 0 ? "+" : ""}{inr(variance.delta)})</span>
                        <Badge variant="outline" className="text-[10px]">
                            Threshold: ±{thresh.total_payroll_mom || 5}% total payroll · ±{thresh.per_employee_mom || 10}% per employee
                        </Badge>
                        {varianceBreached && (
                            <Badge className="bg-rose-100 text-rose-800 text-[10px]" data-testid="preview-variance-breached">
                                Threshold Breached
                            </Badge>
                        )}
                    </div>
                )}
            </div>

            <section className="border rounded-lg bg-white overflow-hidden" data-testid="preview-dept-rollup">
                <header className="px-4 py-2 border-b bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    Departmental Rollup
                </header>
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs text-slate-500">
                        <tr>
                            <th className="px-3 py-2 text-left">Department</th>
                            <th className="px-3 py-2 text-right">Headcount</th>
                            <th className="px-3 py-2 text-right">Gross</th>
                            <th className="px-3 py-2 text-right">Deductions</th>
                            <th className="px-3 py-2 text-right">Net</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {(data.department_rollup || []).map((d) => (
                            <tr key={d.department} data-testid={`dept-row-${d.department.replace(/\s/g, "_")}`}>
                                <td className="px-3 py-2">{d.department}</td>
                                <td className="px-3 py-2 text-right">{d.headcount}</td>
                                <td className="px-3 py-2 text-right font-mono">{inr(d.gross)}</td>
                                <td className="px-3 py-2 text-right font-mono text-rose-700">{inr(d.deductions)}</td>
                                <td className="px-3 py-2 text-right font-mono font-semibold">{inr(d.net)}</td>
                            </tr>
                        ))}
                        {(data.department_rollup || []).length === 0 && (
                            <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">No departments to display.</td></tr>
                        )}
                    </tbody>
                </table>
            </section>

            <section className="border rounded-lg bg-white overflow-hidden" data-testid="preview-component-totals">
                <header className="px-4 py-2 border-b bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    Component Totals
                </header>
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs text-slate-500">
                        <tr>
                            <th className="px-3 py-2 text-left">Component</th>
                            <th className="px-3 py-2 text-right">Total ₹</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {(data.component_totals || []).map((c) => (
                            <tr key={c.component_code}>
                                <td className="px-3 py-2 font-mono text-xs">{c.component_code}</td>
                                <td className="px-3 py-2 text-right font-mono">{inr(c.total_amount)}</td>
                            </tr>
                        ))}
                        {(data.component_totals || []).length === 0 && (
                            <tr><td colSpan={2} className="px-3 py-6 text-center text-slate-400">No component totals to display.</td></tr>
                        )}
                    </tbody>
                </table>
            </section>
        </div>
    );
}
